import "@/styles/base.css";
import { Router } from "@/Router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { STRIPE_PUBLISHABLE_KEY } from "@/lib/common/config";
import { DarkModeProvider } from "@/lib/common/DarkModeContext";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import dayjs from "dayjs";
import Utc from "dayjs/plugin/utc";
import { HTTPEnvironmentProvider } from "@/lib/http";
import { useAppHTTPEnvironment } from "@/lib/common/useAppHTTPEnvironment";
import { AuthStateProvider } from "@/lib/state";
import { StorageProvider } from "@/lib/storage/StorageContext";
import { BrowserStorageService } from "@/lib/storage";
import { ScreenSizeProvider } from "@/lib/common/ScreenSizeContext";
import { UserPreferencesStateProvider } from "@/lib/state/UserPreferencesState";
import { Toaster } from "@/ds/shadcn/components/ui/sonner";

dayjs.extend(Utc);

const browserStorageService = new BrowserStorageService();
const stripeLoadResult = loadStripe(STRIPE_PUBLISHABLE_KEY);

const ELEMENT_OPTIONS = {
	fonts: [
		{
			cssSrc: "https://fonts.googleapis.com/css2?family=Geist:wght@400;600;700&display=swap"
		}
	]
};

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 300 * 1000
		}
	}
});

const Inner = (): React.JSX.Element => (
	<HTTPEnvironmentProvider httpEnvironment={useAppHTTPEnvironment()}>
		<Router />
		<Toaster />
	</HTTPEnvironmentProvider>
);

const root = document.getElementById("root");
if (root) {
	root.classList.add("@container");

	createRoot(root).render(
		<StrictMode>
			<Elements stripe={stripeLoadResult} options={ELEMENT_OPTIONS}>
				<BrowserRouter>
					<QueryClientProvider client={queryClient}>
						<ScreenSizeProvider>
							<DarkModeProvider>
								<StorageProvider storageService={browserStorageService}>
									<UserPreferencesStateProvider>
										<AuthStateProvider>
											<Inner />
										</AuthStateProvider>
									</UserPreferencesStateProvider>
								</StorageProvider>
							</DarkModeProvider>
						</ScreenSizeProvider>
					</QueryClientProvider>
				</BrowserRouter>
			</Elements>
		</StrictMode>
	);
}
